import _ from 'underscore';
import dayjs from '../../../shared/services/dayjs.js';
import stammdatenService from './services/stammdatenService.js';
import { filterListeActiveMA, formatNumber1Decimals, formatNumber2Decimals, loadPreviousPostenSelection, pickContrastForeground, renderFilterListe } from './util.js';
import schichtService from './services/schichtService.js';
import { Egfz } from '../../../../domain/konstanten/Egfz.js';
import { ZeitTyp } from '../../../../domain/konstanten/ZeitTyp.js';
import { feiertagFilter } from '../../../util/util.js';
import basedataService from './services/basedataService.js';
import SystemDialogService from '../../../shared/services/systemDialogService.js';

window.myHandlers = window.myHandlers || {};
window.myVars = window.myVars || {};
window.myHandlers.filterMonatsuebersichtList = filterMonatsuebersichtList;
window.myHandlers.sortMonatsuebersicht = sortMonatsuebersicht;
window.myHandlers.openMonatsuebersichtSettings = openMonatsuebersichtSettings;
window.myHandlers.saveMonatsuebersichtSettings = saveMonatsuebersichtSettings;
window.myHandlers.resetMonatsuebersichtSettings = resetMonatsuebersichtSettings;
window.myVars.monatsuebersichtSortType = 0;

const MU_SETTINGS_KEY = 'mu-settings';
const DEFAULT_MU_SETTINGS = Object.freeze({
    colors: {
        arbeit: '#DAE8F4',
        dienst: '#4aa3df',
        urlaubOffen: '#f2c34d',
        urlaubGenehmigt: '#44c38a',
        krank: '#273e69',
        schule: '#273e69',
        abwesend: '#273e69'
    },
    urlaubMode: 'jahr',
    krankMode: 'jahr'
});
const KRANK_EGFZ_IDS = [Egfz.Krank, Egfz.UnentschuldigtKrank, Egfz.Krank6Wochen, Egfz.KindKrank, Egfz.KO, Egfz.u4K, Egfz.KrankKurzarbeit];

function cloneDefaultSettings() {
    return {
        colors: { ...DEFAULT_MU_SETTINGS.colors },
        urlaubMode: DEFAULT_MU_SETTINGS.urlaubMode,
        krankMode: DEFAULT_MU_SETTINGS.krankMode
    };
}

function normalizeMode(value) {
    return value === 'monat' ? 'monat' : 'jahr';
}

function loadMonatsuebersichtSettings() {
    try {
        const stored = localStorage.getItem(MU_SETTINGS_KEY);
        if (!stored) return cloneDefaultSettings();
        const parsed = JSON.parse(stored);
        return {
            colors: { ...DEFAULT_MU_SETTINGS.colors, ...(parsed.colors || {}) },
            urlaubMode: normalizeMode(parsed.urlaubMode),
            krankMode: normalizeMode(parsed.krankMode)
        };
    } catch (error) {
        console.log(error);
        return cloneDefaultSettings();
    }
}

function applyMonatsuebersichtColors(settings) {
    const root = document.querySelector('.tu-container');
    if (!root) return;
    Object.entries(settings.colors).forEach(([key, hex]) => {
        root.style.setProperty(`--mu-color-${key}`, hex);
        root.style.setProperty(`--mu-fg-${key}`, pickContrastForeground(hex));
    });
}

/**
 * Rendert die Monatsünbersicht für das aktuelle Monatsdatum
 */
async function ladeMonatsuebersicht() {
    document.body.style.cursor = 'wait';
    const zeitraum = window.myVars.aktuellesMonatsDatum;
    const zeitraumStr = zeitraum.format('YYYY-MM-DD');
    const betriebsstaette = stammdatenService.aktuelleBetriebsstaette;

    // Sync UI-Setup (kein await), Settings/Farben/Legende sofort anwenden
    const settings = loadMonatsuebersichtSettings();
    applyMonatsuebersichtColors(settings);
    applyMonatsuebersichtLegend(settings);

    // Vor den Fetches Loader im leeren Grid anzeigen
    showGridLoader();

    // Alle unabhängigen Fetches parallel starten (Filter-UI, Schichten, MA-Liste, Feiertage)
    const [, schichtenMonat, mitarbeiterliste, feiertage] = await Promise.all([
        renderFilterListe(filterMonatsuebersichtList, 'mu-filter-posten-auswahl'),
        schichtService.leseAlleSchichtenMonatsuebersicht(zeitraumStr, betriebsstaette),
        stammdatenService.holeMitarbeiterListe(betriebsstaette, zeitraumStr),
        basedataService.holeFeiertageBasedataProvider()
    ]);

    hideGridLoader();
    renderMitarbeiter(zeitraum, schichtenMonat, mitarbeiterliste, settings);
    // Monatstage nach den Zeilen für die Sonntagsmarkierungen
    renderMonatstage(zeitraum, feiertage);
    // Lade Postenauswahl im Browser
    const postenAktiv = localStorage.getItem('mu-filter-posten-auswahl')?.split(',');
    loadPreviousPostenSelection(postenAktiv, filterMonatsuebersichtList);
    wireRowHover();
    document.body.style.cursor = 'default';
}

/**
 * Zeigt einen Kreis-Loader im leeren Grid, während die Daten geladen werden.
 * Räumt vorher die bestehenden Zeilen ab, damit der Loader auch beim Monatswechsel
 * (nicht nur beim Erst-Render) auf einem leeren Grid sichtbar ist.
 */
function showGridLoader() {
    const gridContainer = document.querySelector('.grid-container');
    if (!gridContainer) return;
    // Bestehende MA-Zeilen in einem Rutsch entfernen (1 Reflow)
    const oldRows = gridContainer.querySelectorAll('.grid-row');
    if (oldRows.length > 0) {
        const range = document.createRange();
        range.setStartBefore(oldRows[0]);
        range.setEndAfter(oldRows[oldRows.length - 1]);
        range.deleteContents();
    }
    if (gridContainer.querySelector('.mu-grid-loader')) return;
    const loader = document.createElement('div');
    loader.className = 'mu-grid-loader';
    gridContainer.appendChild(loader);
}

function hideGridLoader() {
    document.querySelector('.grid-container .mu-grid-loader')?.remove();
}

/**
 * Hebt die ganze MA-Zeile beim Hovern hervor (Cells einer Zeile haben das gleiche data-ma Attribut).
 */
function wireRowHover() {
    const gridContainer = document.querySelector('.grid-container');
    if (!gridContainer || gridContainer.dataset.hoverWired === '1') return;
    gridContainer.dataset.hoverWired = '1';
    let hoveredMA = null;
    let hoveredCells = null;

    const clearHover = () => {
        if (!hoveredCells) return;
        hoveredCells.forEach((el) => el.classList.remove('mu-row-hover'));
        hoveredCells = null;
        hoveredMA = null;
    };

    gridContainer.addEventListener('mouseover', (event) => {
        const ma = event.target.closest('.grid-row')?.getAttribute('data-ma');
        if (ma === hoveredMA) return;
        clearHover();
        if (!ma) return;
        hoveredCells = gridContainer.querySelectorAll(`.grid-row[data-ma="${ma}"]`);
        hoveredCells.forEach((el) => el.classList.add('mu-row-hover'));
        hoveredMA = ma;
    });
    gridContainer.addEventListener('mouseleave', clearHover);
}

function applyMonatsuebersichtLegend(settings) {
    const urlaub = document.getElementById('mu-legend-urlaub');
    const krank = document.getElementById('mu-legend-krank');
    if (urlaub) {
        urlaub.title = settings.urlaubMode === 'monat'
            ? 'Genommene Urlaubstage (Monat)'
            : 'Verbleibende Urlaubstage (Jahr)';
    }
    if (krank) {
        krank.title = settings.krankMode === 'monat'
            ? 'Kranktage (Monat)'
            : 'Kranktage (Jahr)';
    }
}


/**
 * Damit Filter kombinierbar sind, müssen wir immer beide anwenden.
 */
function filterMonatsuebersichtList() {
    filterPosten();
    filterEGFZ();
}

function filterPosten() {
    // filter posten
    const aktivePosten = [];
    // sammle alle aktiven Posten
    document.querySelectorAll('.tu-filter-child').forEach((child) => {
        if (child.querySelector('.tu-filter-child-input').checked) {
            aktivePosten.push(child.id);
        }
    });
    document.querySelectorAll('.grid-item.grid-row').forEach((item) => {
        // todo: check multiple posten...
        item.style.display = !aktivePosten.some((posten) => item.getAttribute('data-posten').split(',').includes(posten)) ? item.classList.add('hidden') : item.classList.remove('hidden');
    });
    // Speichere Postenauswahl im Browser
    localStorage.setItem('mu-filter-posten-auswahl', aktivePosten);
}

function filterEGFZ() {
    // überprüfe Filterauswahl
    const filterArbeit = document.getElementById('mu-filter-arbeit').checked;
    const filterDienst = document.getElementById('mu-filter-dienst').checked;
    const filterUrlaubOffen = document.getElementById('mu-filter-u-offen').checked;
    const filterUrlaubGenehmigt = document.getElementById('mu-filter-u-genehmigt').checked;
    const filterKrank = document.getElementById('mu-filter-krank').checked;
    const filterSchule = document.getElementById('mu-filter-schule').checked;
    const filterEGFZ = document.getElementById('mu-filter-egfz').checked;
    const filterLeer = document.getElementById('mu-filter-leer').checked;
    const aktiveItems = [...document.querySelectorAll('.grid-item.grid-row:not(.hidden)')];

    function filterCheck(item) {
        let filterMatch = false;
        filterMatch = filterArbeit ? filterMatch || item.getAttribute('data-arbeit') == '1' : filterMatch;
        filterMatch = filterDienst ? filterMatch || item.getAttribute('data-dienst') == '1' : filterMatch;
        filterMatch = filterUrlaubOffen ? filterMatch || item.getAttribute('data-urlaub') == '0' : filterMatch;
        filterMatch = filterUrlaubGenehmigt ? filterMatch || item.getAttribute('data-urlaub') == '1' : filterMatch;
        filterMatch = filterKrank ? filterMatch || item.getAttribute('data-krank') == '1' : filterMatch;
        filterMatch = filterSchule ? filterMatch || item.getAttribute('data-schule') == '1' : filterMatch;
        filterMatch = filterEGFZ ? filterMatch || item.getAttribute('data-egfz') == '1' : filterMatch;
        return filterMatch;
    }

    // Wir iterieren durch alle Items, und überspringen die row wenn mind. 1 Item die Filterkriterien erfüllt...
    let currentRow = 0;
    let anzahlMA = 0;
    const rowLength = 35;
    let emptyRow = [];

    for (let i = 0; i < aktiveItems.length; i++) {
        const currentItem = aktiveItems[i];
        const filterMatch = filterCheck(currentItem);
        // Leere items merken, die ersten 4 Spalten sind immer befüllt
        if (currentItem.innerHTML == '' || (i+1) % rowLength <= 4) {
            emptyRow.push(currentItem);
        }
        // Wenn wir den Filter erfüllen können wir zur nächsten Zeile springen
        if (filterMatch) {
            currentRow++;
            anzahlMA++;
            i = currentRow * rowLength - 1; // -1 da i++ bei continue eins drauf addiert wird in der nächsten Iteration
            emptyRow = [];
            continue;
        }
        // Wenn wir am Ende der Zeile sind, wird die Zeile versteckt (außer ganz Leere Zeile mit Leer Filter aktiv)
        if ((i+1) % rowLength === 0) {
            if (!filterLeer || emptyRow.length !== rowLength) {
                let sibling = currentItem;
                for (let j = 0; j < rowLength;  j++) {
                    sibling.classList.add('hidden');
                    sibling = sibling.previousElementSibling;
                }
            } else {
                anzahlMA++;
            }
            currentRow++;
            emptyRow = [];
        }
    }
    document.getElementById('mu-anzahl-ma').innerText = `(${anzahlMA})`;
}

/**
 * Rendert den Header der Monatsübersicht mit Datum
 * @param {dayjs} zeitraum in deutsche Zeitzone!
 * @param {Array} feiertage vorgeladen aus ladeMonatsuebersicht
 */
function renderMonatstage(zeitraum, feiertage) {
    const aktuelleBS = stammdatenService.unternehmensobjekt.Betriebsstaette.find((bs) => bs._id === stammdatenService.aktuelleBetriebsstaette);
    const feiertageBundesland = _.isEmpty(aktuelleBS) ? feiertage : feiertagFilter(feiertage, aktuelleBS.Bundesland, aktuelleBS.Feiertage);

    const daysInMonth = zeitraum.daysInMonth();
    const datumLegend = document.querySelectorAll('.grid-legend.grid-datum');
    const zeilenElemente = [...document.querySelectorAll('.grid-item:not(.grid-legend, .grid-filter)')];
    const zeilenAnzahl = zeilenElemente.length / 35;
    const sonntagIndices = [];
    const feiertagIndices = [];
    for (let i = 0; i < daysInMonth; i++) {
        const tmpDate = zeitraum.startOf('month').add(i, 'days');
        const tmpElem = datumLegend[i];
        tmpElem.innerText = `${tmpDate.format('dd')}\n${i+1}.`;
        // Sonntage merken
        if (sonntagIndices.length <= 0 && tmpDate.day() == 0) {
            for (let sonntag = tmpDate.date(); sonntag <= daysInMonth; sonntag+=7) {
                sonntagIndices.push(sonntag + 3);
            }   
        }
        // Feiertage merken
        const feiertag = feiertageBundesland.find((feiertag) => {
            return feiertag.dateFormat == tmpDate.format('DD.MM.YYYY');
        });
        if (feiertag) {
            feiertagIndices.push(_.extend(feiertag, { index: tmpDate.date() + 3 }));
        }
    }
    // Falls der Monat weniger als 31 Tage hat müssen wir beim Monatwechsel die Zellen leeren
    for (let i = daysInMonth; i < 31; i++) {
        const tmpElem = datumLegend[i];
        tmpElem.innerText = '';
    }

    // Sonntage farblich markieren...
    for (let z = 0; z < zeilenAnzahl; z++) {
        for (const s of sonntagIndices) {
            zeilenElemente[z * 35 + s].classList.add('grid-sunday');
        }
        for (const f of feiertagIndices) {
            zeilenElemente[z * 35 + f.index].classList.add('grid-feiertag');
            zeilenElemente[z * 35 + f.index].title = `${zeilenElemente[z * 35 + f.index].title} (${f.fname})`.trim();
        }
    }
    document.getElementById('mu-title').innerText = `${zeitraum.format('MMMM YYYY')}`;
}

/**
 * Rendert die Mitarbeiterzeilen in die Monatsübersicht inklusive Urlaubsanträge und Schichten
 * @param {dayjs} zeitraum
 * @param {Array} schichtenMonat
 * @param {Array} mitarbeiterliste vorgeladen aus ladeMonatsuebersicht
 * @param {Object} settings
 */
function renderMitarbeiter(zeitraum, schichtenMonat, mitarbeiterliste, settings) {
    const gridMonat = document.querySelector('.grid-container');
    // Alte Reihen in einem Rutsch entfernen (1 Reflow statt N)
    const oldRows = gridMonat.querySelectorAll('.grid-row');
    if (oldRows.length > 0) {
        const range = document.createRange();
        range.setStartBefore(oldRows[0]);
        range.setEndAfter(oldRows[oldRows.length - 1]);
        range.deleteContents();
    }
    // Mitarbeiter Liste filtern & sortieren
    mitarbeiterliste = filterListeActiveMA(mitarbeiterliste);
    mitarbeiterliste = mitarbeiterliste.filter((ma) => !ma?.Archiviert);
    sortMonatsuebersichtMitarbeiter(mitarbeiterliste);
    // Schichten einmal pro MA gruppieren (statt N x .filter() = O(N*M))
    const schichtenProMA = new Map();
    schichtenMonat.forEach((schicht) => {
        const id = String(schicht.MitarbeiterID);
        if (!schichtenProMA.has(id)) schichtenProMA.set(id, []);
        schichtenProMA.get(id).push(schicht);
    });
    const gridTemplateMA = document.querySelector('[grid-row-template]');
    const wantsMonatAbw = settings.urlaubMode === 'monat' || settings.krankMode === 'monat';
    // Alle Zeilen in einem DocumentFragment aufbauen und einmal anhängen
    const fragment = document.createDocumentFragment();
    mitarbeiterliste.forEach((ma) => {
        const newRow = gridTemplateMA.content.cloneNode(true);
        const nameCell = newRow.querySelector('.grid-name');
        nameCell.innerText = `${ma.Nachname}, ${ma.Vorname}`;
        nameCell.title = `${ma.Nachname}, ${ma.Vorname} (${ma.Personalnummer})`;
        nameCell.addEventListener('click', () => {
            window.myHandlers.selectMA(ma._id);
        });
        const schichtenMA = schichtenProMA.get(String(ma._id)) || [];
        newRow.querySelector('.grid-gz').innerText = `${formatNumber1Decimals(ma.Ueberstunden, false)}`;
        const monatAbw = wantsMonatAbw ? berechneAbwesenheitenMonat(schichtenMA) : null;
        newRow.querySelector('.grid-urlaub').innerText = settings.urlaubMode === 'monat'
            ? `${monatAbw.urlaubTage}`
            : `${ma.Resturlaub}`;
        newRow.querySelector('.grid-krank').innerText = settings.krankMode === 'monat'
            ? `${monatAbw.kranktage}`
            : `${ma.Kranktage}`;
        const postenIDs = _.pluck(ma.Posten, 'PostenID').join(',');
        const maID = ma._id;
        [...newRow.children].forEach((child) => {
            child.setAttribute('data-posten', postenIDs);
            child.setAttribute('data-ma', maID);
        });
        renderUrlaubzeitraumMA(ma, newRow, zeitraum);
        renderSchichten(schichtenMA, newRow);
        fragment.appendChild(newRow);
    });
    gridMonat.appendChild(fragment);
}

/**
 * Zählt Urlaubs- und Kranktage in diesem Monat aus den Schichten - für sumMode "monat".
 * Urlaub zählt nur tatsächliche Urlaubsschichten (Egfz.Urlaub), keine offenen/genehmigten Anträge.
 */
function berechneAbwesenheitenMonat(schichtenMA) {
    const urlaubTageSet = new Set();
    const kranktageSet = new Set();
    schichtenMA.forEach((schicht) => {
        if (schicht.ZeitTyp !== ZeitTyp.EGFZ) return;
        const egfzID = schicht.Zeitbloecke?.[0]?.EgfzID;
        const datum = dayjs(schicht.Datum).tz().format('YYYY-MM-DD');
        if (egfzID === Egfz.Urlaub) {
            urlaubTageSet.add(datum);
        } else if (KRANK_EGFZ_IDS.includes(egfzID)) {
            kranktageSet.add(datum);
        }
    });
    return { urlaubTage: urlaubTageSet.size, kranktage: kranktageSet.size };
}

function sortMonatsuebersichtMitarbeiter(mitarbeiterliste) {
    switch(window.myVars.monatsuebersichtSortType) {
        case 1:
            // Nachname
            mitarbeiterliste.sort((a, b) => (a.Nachname + a.Vorname).localeCompare((b.Nachname + b.Vorname)));
            break;
        case 2:
            // PNR
            mitarbeiterliste.sort((a, b) => {
                if (a.Personalnummer === '') {
                    return 1; // Move empty strings to the end
                } else if (b.Personalnummer === '') {
                    return -1; // Move empty strings to the end
                } else {
                    const numA = parseInt(a.Personalnummer, 10);
                    const numB = parseInt(b.Personalnummer, 10);
                    // falls eine Pnr keine reine Nummer ist, müssen wir diese anders behandeln...
                    if (isNaN(numA) || isNaN(numB)) {
                        return isNaN(numA) ? 1 : -1;
                    }
                    return numA - numB;
                }
            });
            break;
        case 3:
            // Sortierreihenfolge
            mitarbeiterliste.sort((a, b) => a.Sortierreihenfolge - b.Sortierreihenfolge || (a.Nachname + a.Vorname).localeCompare((b.Nachname + b.Vorname)));
            break;
    }
}

/**
 * Rendert den Mitarbeiter Urlaub in die Mitarbeiter Zeile der Monatsübersicht
 * @param {*} mitarbeiter kleines Mitarbeiterobjekt aus der Mitarbeiterliste
 * @param {HTMLElement} maZeile 
 * @param {dayjs} zeitraum in deutscher Zeitzone!
 */
function renderUrlaubzeitraumMA(mitarbeiter, maZeile, zeitraum) {
    const urlaubsantraege = mitarbeiter.Urlaubsantraege;
    const urlaubtemplate = document.querySelector('[mu-urlaub-hover-template]');
    urlaubsantraege.forEach((urlaub) => {
        // Wir checken ob der Urlaub in diesem Monatszeitraum fällt, wenn nicht skippen wir...
        const urlaubVonDayjs = dayjs(urlaub.UrlaubVon).tz(); // DEUTSCHE ZEITZONE
        const urlaubBisDayjs = dayjs(urlaub.UrlaubBis).tz(); // DEUTSCHE ZEITZONE
        const urlaubImZeitraum = zeitraum.isSame(urlaubVonDayjs, 'month') || zeitraum.isSame(urlaubBisDayjs, 'month');
        if (!urlaubImZeitraum || urlaub.UrlaubStatus === 'abgelehnt') return;
        const urlaubAnfang = dayjs.max(zeitraum.startOf('month'), urlaubVonDayjs);
        const urlaubEnde = dayjs.min(zeitraum.endOf('month'), urlaubBisDayjs);
        const zeileArray = [...maZeile.children];
        const istOffenerUrlaubsantrag = urlaub.UrlaubStatus == 'offen';
        const urlaubTitle = `${istOffenerUrlaubsantrag ? 'Offener' : 'Genehmigter'} Urlaubsantrag vom ${urlaubVonDayjs.format('DD.MM.')} - ${urlaubBisDayjs.format('DD.MM.YYYY')}`;
        for (let date = urlaubAnfang.date(); date <= urlaubEnde.date(); date++) {
            // +3 wegen den ersten 4 Spalten [0, 1, 2, 3] => damit ist der 1. des Monats bei 4
            const tagItem = zeileArray[date + 3];
            tagItem.innerHTML = '<i class="bi bi-airplane-fill rotate45"></i>';
            tagItem.style.backgroundColor = istOffenerUrlaubsantrag ? 'var(--mu-color-urlaubOffen)' : 'var(--mu-color-urlaubGenehmigt)';
            tagItem.style.color = istOffenerUrlaubsantrag ? 'var(--mu-fg-urlaubOffen)' : 'var(--mu-fg-urlaubGenehmigt)';
            tagItem.title = urlaubTitle;
            tagItem.setAttribute('data-urlaub', istOffenerUrlaubsantrag ? '0' : '1');
            tagItem.setAttribute('urlaub-id', urlaub._id);
            tagItem.setAttribute('data-count', '1');
            tagItem.classList.add('mu-urlaubsantrag');
            // Hover effekt zum genehmigen/ablehnen
            if (istOffenerUrlaubsantrag) {
                // Falls es offene urlaubsanträge gibt, erstellen eine Bearbeitungsfunktion mit einem Hover Effekt
                const urlaubInfo = urlaubtemplate.content.cloneNode(true).children[0];
                const urlaubZeitraum = urlaubInfo.children[0];
                urlaubZeitraum.innerText = `Offener Urlaubsantrag vom ${urlaubVonDayjs.format('DD.MM.')} - ${urlaubBisDayjs.format('DD.MM.YYYY')}`;
                const aktionen = urlaubInfo.children[1];
                const aktionJA = aktionen.children[0];
                const aktionNein = aktionen.children[1];
                aktionJA.addEventListener('click', async () => {
                    const result = await schichtService.erstelleUrlaubAusAntrag(mitarbeiter._id, urlaub._id, 'genehmigt');
                    if (!result) return;
                    // Bei Erfolg Urlaub in grün umwandeln
                    document.querySelectorAll(`[urlaub-id="${urlaub._id}"]`).forEach((item) => {
                        item.setAttribute('data-urlaub', '1');
                        item.title =`Genehmigter Urlaubsantrag vom ${urlaubVonDayjs.format('DD.MM.')} - ${urlaubBisDayjs.format('DD.MM.YYYY')}`;
                        item.style.backgroundColor = 'var(--mu-color-urlaubGenehmigt)';
                        item.style.color = 'var(--mu-fg-urlaubGenehmigt)';
                        item.classList.remove('mu-urlaub-offen');
                        item.querySelector('.mu-urlaub-div').remove();
                    });
                });
                aktionNein.addEventListener('click', async () => {
                    const result = await stammdatenService.ablehnenUrlaubsantrag(mitarbeiter._id, urlaub._id);
                    if (!result) return;
                    // Bei Erfolg Urlaub entfernen
                    document.querySelectorAll(`[urlaub-id="${urlaub._id}"]`).forEach((item) => {
                        item.innerHTML = '';
                        item.title = '';
                        item.style.backgroundColor = 'unset';
                        item.style.color = '';
                        const dataCount = item.getAttribute('data-count');
                        item.setAttribute('data-count', dataCount ? parseInt(dataCount) - 1 : 0);
                        item.setAttribute('data-urlaub', '-1');
                    });
                });
                tagItem.classList.add('mu-urlaub-offen');
                tagItem.appendChild(urlaubInfo);
            }
        }
    });
}

function renderSchichten(schichten, maZeile) {
    const zeileArray = [...maZeile.children];
    schichten.forEach((schicht) => {
        const schichtDatum = dayjs(schicht.Datum).tz();
        const tagItem = zeileArray[schichtDatum.date() + 3];
        // Wenn wir den Tag nicht finden überspringen wir ihn...
        if (!tagItem || (tagItem.innerHTML !== '' && schicht.Zeitbloecke[0].EgfzID === Egfz.Urlaub)) return;
        tagItem.innerHTML = tagItem.innerHTML ? tagItem.innerHTML : '<i class="bi bi-clock-fill"></i>';
        // Unterscheide Zeiterfassung und EGFZ Zeiten
        if (schicht.ZeitTyp > ZeitTyp.EGFZ) {
            // Zeiterfassung (manuell oder stempel)
            tagItem.title = `${tagItem.title} ${schicht?.Titel ? schicht.Titel : 'Arbeit'} (${dayjs(schicht.SchichtVon).tz().format('HH:mm')} - ${dayjs(schicht.SchichtBis).tz().format('HH:mm')})`.trim();
            tagItem.style.backgroundColor = 'var(--mu-color-arbeit)';
            tagItem.style.color = 'var(--mu-fg-arbeit)';
            tagItem.setAttribute('data-arbeit', '1');
        } else if (schicht.ZeitTyp == ZeitTyp.EGFZ) {
            // EGFZ Zeiten (weitere Unterscheidung zwischen GZ+- und EGFZ)
            if (schicht.Zeitbloecke[0].EgfzID === Egfz.GZAufbau) {
                tagItem.style.backgroundColor = 'var(--custom-light-orange)';
                tagItem.title = `${tagItem.title} GZ+ (${dayjs(schicht.SchichtVon).tz().format('HH:mm')} - ${dayjs(schicht.SchichtBis).tz().format('HH:mm')})`.trim();
                tagItem.setAttribute('data-arbeit', '1');
            } else if (schicht.Zeitbloecke[0].EgfzID === Egfz.GZAbbau) {
                tagItem.style.backgroundColor = 'var(--custom-light-pink)';
                tagItem.title = `${tagItem.title} GZ- (${dayjs(schicht.SchichtVon).tz().format('HH:mm')} - ${dayjs(schicht.SchichtBis).tz().format('HH:mm')})`.trim();
                tagItem.setAttribute('data-arbeit', '1');
            } else {
                tagItem.title = `${tagItem.title} ${schicht?.Titel ? 'EGFZ ' + schicht.Titel : 'EGFZ'}`;
                if (KRANK_EGFZ_IDS.includes(schicht.Zeitbloecke[0].EgfzID)) {
                    tagItem.innerHTML = '<i class="bi bi-clipboard-plus"></i>';
                    tagItem.style.backgroundColor = 'var(--mu-color-krank)';
                    tagItem.style.color = 'var(--mu-fg-krank)';
                    tagItem.setAttribute('data-krank', '1');
                } else if (schicht.Zeitbloecke[0].EgfzID === Egfz.Schule) {
                    tagItem.innerHTML = '<i class="bi bi-book"></i>';
                    tagItem.style.backgroundColor = 'var(--mu-color-schule)';
                    tagItem.style.color = 'var(--mu-fg-schule)';
                    tagItem.setAttribute('data-schule', '1');
                } else {
                    tagItem.innerHTML = '<i class="bi bi-slash-circle-fill"></i>';
                    tagItem.style.backgroundColor = 'var(--mu-color-abwesend)';
                    tagItem.style.color = 'var(--mu-fg-abwesend)';
                    tagItem.setAttribute('data-egfz', '1');
                }
            }
        } else if (schicht.ZeitTyp == ZeitTyp.Dienstplan) {
            // Dienstplan
            tagItem.title = `${tagItem.title} ${schicht?.Titel ? schicht.Titel : 'Dienstplan'} (${dayjs(schicht.SchichtVon).tz().format('HH:mm')} - ${dayjs(schicht.SchichtBis).tz().format('HH:mm')})`.trim();
            tagItem.style.backgroundColor = 'var(--mu-color-dienst)';
            tagItem.style.color = 'var(--mu-fg-dienst)';
            tagItem.innerHTML = '<i class="bi bi-calendar-week"></i>';
            tagItem.setAttribute('data-dienst', '1');
        }
        // Allgemein Schicht Counter
        const tagCounter = tagItem.getAttribute('data-count');
        const newCount = tagCounter ? parseInt(tagCounter) + 1 : '1';
        tagItem.setAttribute('data-count', newCount);
        if (newCount >= 2) {
            let bubble = tagItem.querySelector('.grid-row-bubble');
            // Check ob das bubble Element bereits exisitiert
            if (!bubble) {
                // Erstelle bubble element
                bubble = document.createElement('div');
                bubble.classList.add('grid-row-bubble');
                tagItem.appendChild(bubble);
            }
            // Aktuellen Schichten Count eingeben
            bubble.innerText = newCount;
        }
    });
}

/**
 * Sortiert die Monatsübersicht nach:
 * - 1: Nachname
 * - 2: Personalnummer
 * - 3: Sortierreihenfolge
 * @param {Number} sortType
 */
async function sortMonatsuebersicht(sortType) {
    window.myVars.monatsuebersichtSortType = sortType;
    await ladeMonatsuebersicht();
}

function populateMonatsuebersichtSettingsDialog(settings) {
    Object.entries(settings.colors).forEach(([key, hex]) => {
        const input = document.getElementById(`mu-settings-color-${key}`);
        if (input) input.value = hex;
    });
    const urlaubSel = document.getElementById('mu-settings-urlaub-mode');
    if (urlaubSel) urlaubSel.value = settings.urlaubMode;
    const krankSel = document.getElementById('mu-settings-krank-mode');
    if (krankSel) krankSel.value = settings.krankMode;
}

function readMonatsuebersichtSettingsFromDialog() {
    const settings = cloneDefaultSettings();
    Object.keys(DEFAULT_MU_SETTINGS.colors).forEach((key) => {
        const input = document.getElementById(`mu-settings-color-${key}`);
        if (input && input.value) settings.colors[key] = input.value;
    });
    const urlaubSel = document.getElementById('mu-settings-urlaub-mode');
    if (urlaubSel) settings.urlaubMode = normalizeMode(urlaubSel.value);
    const krankSel = document.getElementById('mu-settings-krank-mode');
    if (krankSel) settings.krankMode = normalizeMode(krankSel.value);
    return settings;
}

async function openMonatsuebersichtSettings() {
    populateMonatsuebersichtSettingsDialog(loadMonatsuebersichtSettings());
    await SystemDialogService.instance.displayAsync('mu-settings-dialog');
}

async function saveMonatsuebersichtSettings() {
    const settings = readMonatsuebersichtSettingsFromDialog();
    localStorage.setItem(MU_SETTINGS_KEY, JSON.stringify(settings));
    SystemDialogService.instance.confirm();
    await ladeMonatsuebersicht();
}

function resetMonatsuebersichtSettings() {
    populateMonatsuebersichtSettingsDialog(cloneDefaultSettings());
}

export {
    ladeMonatsuebersicht
};
